import * as B from '@blueprintjs/core';
import * as Sentry from '@sentry/react';
import classnames from 'classnames';
import {saveAs} from 'file-saver';
import * as I from 'immutable';
import moment from 'moment';
import React, {ReactNode} from 'react';
import ReactDOM from 'react-dom';
import {useNavigate} from 'react-router-dom';

import {usePushNotification} from 'app/components/Notification';
import {PROPERTY_DETAILS_LAYER_LEGEND_SECTION_ID} from 'app/components/PropertyDetailsSidebar/constants';
import cs from 'app/components/PropertyDetailsSidebar/PropertyDetailsSidebar.module.scss';
import {AssigneeMenu} from 'app/components/PropertyEditors/AssigneeMenu';
import {DueDateMenu} from 'app/components/PropertyEditors/DueDateMenu';
import {ParcelReportWindow} from 'app/components/ReportExport/ParcelReportWindow';
import {ReportPortalContext} from 'app/components/ReportExport/ReportPortalProvider';
import {
  SidebarEmptyValue,
  SidebarFieldGroup,
  SidebarFieldRow,
  SidebarHeader,
  SidebarPanel,
  SidebarParagraph,
  SidebarSection,
} from 'app/components/Sidebar';
import {api} from 'app/modules/Remote';
import {OverlayConfig} from 'app/modules/Remote/api';
import {ApiFeature, ApiFeatureData} from 'app/modules/Remote/Feature';
import {ApiFeatureCollection} from 'app/modules/Remote/FeatureCollection';
import {ApiOrganization, ApiOrganizationUser, getAreaUnit} from 'app/modules/Remote/Organization';
import {ApiProject} from 'app/modules/Remote/Project';
import CustomizeTagsModal from 'app/pages/MonitorProjectView/CustomizeTagsDialog';
import EditTagsMenu from 'app/pages/MonitorProjectView/EditTagsMenu';
import {LoggedInUserActions} from 'app/providers/AuthProvider';
import {useFeatureDataForFeature} from 'app/providers/FeatureDataProvider';
import {FeaturesActions} from 'app/providers/FeaturesProvider';
import {recordEvent} from 'app/tools/Analytics';
import * as CONSTANTS from 'app/utils/constants';
import {APP_PROPERTIES_KEY, UNIT_AREA_M2} from 'app/utils/constants';
import * as conversionUtils from 'app/utils/conversionUtils';
import {featureInRegridCoverage} from 'app/utils/featureCollectionUtils';
import {useUserInfo} from 'app/utils/featureFlags';
import * as featureUtils from 'app/utils/featureUtils';
import {UTMArea} from 'app/utils/geoJsonUtils';
import {useStateWithDeps} from 'app/utils/hookUtils';
import * as imageryUtils from 'app/utils/imageryUtils';
import {LayerInfo, PLANET_BASEMAP_HIGH_RES_TRUECOLOR} from 'app/utils/layers';
import * as layerUtils from 'app/utils/layerUtils';
import * as mapUtils from 'app/utils/mapUtils';
import * as multiFeaturePropertyUtils from 'app/utils/multiFeaturePropertyUtils';
import * as routeUtils from 'app/utils/routeUtils';
import * as tagUtils from 'app/utils/tagUtils';
import * as userUtils from 'app/utils/userUtils';

const downloadPendingMessage =
  "We're on it! You'll receive an email with a download link within an hour.";

// Webtile-only sources whose GeoTIFF export is stitched from imagery-central webtiles rather than
// COGs. Mirrors GEOTIFF_EXPORT_WEBTILE_SOURCES in backend/common/cog_export_utils.py.
const GEOTIFF_EXPORT_WEBTILE_SOURCES = ['WMTS-TRUECOLOR'];

const EXPORT_HELP_URL = 'https://lens.tech/support/article/81-exporting-your-imagery-orders';
const STREAMING_HELP_URL = 'https://lens.tech/support/article/127-wmts-streaming';

/**
 * The datum backing an image on the map. Returns undefined while switching
 * properties — imageRefs and featureData take a few renders to agree.
 */
const findFeatureDatumForImageRef = (
  featureData: I.ImmutableOf<ApiFeatureData[]> | null,
  featureCollection: I.ImmutableOf<ApiFeatureCollection>,
  imageRef: mapUtils.MapImageRef
) => {
  if (!featureData) {
    return undefined;
  }
  const cursorKey = featureUtils.getCursorKeyForLayerKey(featureCollection, imageRef.layerKey);
  return featureData.findLast(
    (d) => d!.get('types').includes(imageRef.layerKey) && d!.get(cursorKey) === imageRef.cursor
  );
};

// The exported Blueprint AnchorButtonProps type is not surfacing instrinsic
// HTMLAnchorElement attributes, meaning that it’s not recognizing href as a
// valid prop. Thus, we infer the component props instead.
//
// TODO(emily): Consider extracting this component for usage more broadly in the
// application. The AppNav, OrderImagerySidebar, and LayersMenu
// components/directories all have similar instantiations.
export const InfoIconButton: React.FunctionComponent<
  React.PropsWithChildren<Partial<React.ComponentProps<typeof B.AnchorButton>>>
> = (buttonProps) => (
  <B.AnchorButton icon="info-sign" minimal small target="_blank" {...buttonProps} />
);

const LearnMoreButton: React.FunctionComponent<{href: string}> = ({href}) => (
  <B.Tooltip content="Learn more">
    <InfoIconButton href={href} />
  </B.Tooltip>
);

/** One export format inside a layer’s Downloads block. */
const DownloadFormat: React.FunctionComponent<
  React.PropsWithChildren<{
    title: string;
    note: React.ReactNode;
    onDownload: () => void;
    isLoading?: boolean;
    isDisabled?: boolean;
    disabledTooltip?: string;
    error?: string | null;
    includeOverlays?: {checked: boolean; onChange: (checked: boolean) => void};
  }>
> = ({
  title,
  note,
  onDownload,
  isLoading,
  isDisabled,
  disabledTooltip,
  error,
  includeOverlays,
  children,
}) => (
  <div className={cs.downloadFormat}>
    <div className={cs.downloadFormatRow}>
      <span className={cs.downloadFormatTitle}>{title}</span>
      <B.Tooltip content={disabledTooltip} disabled={!disabledTooltip}>
        <B.Button
          minimal
          small
          intent={B.Intent.PRIMARY}
          text="Download"
          onClick={onDownload}
          loading={isLoading}
          disabled={isDisabled}
        />
      </B.Tooltip>
    </div>
    <SidebarParagraph className={cs.fieldNote}>{note}</SidebarParagraph>
    {children}
    {includeOverlays && (
      <B.Checkbox
        className={cs.downloadOverlays}
        checked={includeOverlays.checked}
        onChange={(e) => includeOverlays.onChange((e.target as HTMLInputElement).checked)}
        label="Include overlays"
      />
    )}
    {error && <div className={cs.downloadError}>{error}</div>}
  </div>
);

const TagField: React.FunctionComponent<
  React.PropsWithChildren<{
    title: string;
    features: I.ImmutableOf<ApiFeature[]>;
    featureCollection: I.ImmutableOf<ApiFeatureCollection>;
    batchPatchFeature: FeaturesActions['batchPatchFeature'];
    role: ApiOrganizationUser['role'];
    openCustomizeTagsModal: () => void;
    organization: I.ImmutableOf<ApiOrganization>;
  }>
> = ({
  title,
  featureCollection,
  features,
  role,
  organization,
  batchPatchFeature,
  openCustomizeTagsModal,
}) => {
  const tagSettings = tagUtils.getTagSettings(organization, tagUtils.TAG_KIND.FEATURE);
  const tagIds = tagUtils.getFeaturesTagIds(features);

  const featureTagSettings = tagSettings.keySeq().filter((t) => tagIds.includes(t!));
  const canEditTags = userUtils.isOwnerOrAdmin(role) || role === 'regular';

  return (
    <SidebarFieldRow
      label={title}
      trailingAction={
        canEditTags ? (
          <EditTagsMenu
            featureCollectionId={featureCollection.get('id')}
            tagSettings={tagSettings}
            tagStatusMaps={tagUtils.getFeaturesTagStatusMaps(features)}
            onChange={(tagStatusMap) =>
              batchPatchFeature({
                featureCollectionId: featureCollection.get('id'),
                features,
                changes: featureUtils.tagStatusMapToPropertiesChanges(tagStatusMap),
              })
            }
            openCustomizeTagsModal={canEditTags ? openCustomizeTagsModal : null}
            arrow
            kind={tagUtils.TAG_KIND.FEATURE}
          >
            <B.Button icon="edit" minimal small />
          </EditTagsMenu>
        ) : undefined
      }
    >
      <div className={cs.tagsContent}>
        {featureTagSettings
          .map((id) => <tagUtils.Tag key={id} setting={tagSettings.get(id!)} />)
          .toArray()}

        {featureTagSettings.isEmpty() && <SidebarEmptyValue />}
      </div>
    </SidebarFieldRow>
  );
};

const AssigneeField: React.FunctionComponent<
  React.PropsWithChildren<{
    title: string;
    organizationUsers: ApiOrganizationUser[];
    emails: string[];
    featureCollection: I.ImmutableOf<ApiFeatureCollection>;
    features: I.ImmutableOf<ApiFeature[]>;
    batchPatchFeature: FeaturesActions['batchPatchFeature'];
    canEdit: boolean;
  }>
> = ({
  title,
  organizationUsers,
  emails,
  featureCollection,
  features,
  batchPatchFeature,
  canEdit,
}) => {
  const value = emails
    .map((email) => {
      const user = organizationUsers.find((u) => u.email === email);
      return user?.name ? userUtils.getSuffixedUserName(user) : email;
    })
    .join(', ');

  return (
    <SidebarFieldRow
      label={title}
      trailingAction={
        canEdit ? (
          <AssigneeMenu
            featureCollection={featureCollection}
            features={features}
            organizationUsers={organizationUsers}
            batchPatchFeature={batchPatchFeature}
            menuClassName={cs.assigneeMenu}
          >
            <B.Tooltip content={`Edit ${title.toLocaleLowerCase()}`}>
              <B.Button icon="edit" minimal small />
            </B.Tooltip>
          </AssigneeMenu>
        ) : undefined
      }
    >
      {value || <SidebarEmptyValue />}
    </SidebarFieldRow>
  );
};

const SourceInformation: React.FunctionComponent<
  React.PropsWithChildren<{
    position: 'left' | 'right' | 'single';
    imageRef: mapUtils.MapImageRef;
    organization: I.ImmutableOf<ApiOrganization>;
    featureCollection: I.ImmutableOf<ApiFeatureCollection>;
    selectedFeature: I.ImmutableOf<ApiFeature>;
    visibleOverlayConfigs: OverlayConfig[];
  }>
> = ({
  position,
  imageRef,
  organization,
  featureCollection,
  selectedFeature,
  visibleOverlayConfigs,
}) => {
  const featureData = useFeatureDataForFeature(selectedFeature?.get('id'));
  const [_, profile] = useUserInfo();
  const featureDatum = React.useMemo(
    () => findFeatureDatumForImageRef(featureData, featureCollection, imageRef),
    [featureCollection, featureData, imageRef]
  );

  const [isDownloadingPng, setIsDownloadingPng] = React.useState(false);
  const [isDownloadingGeoPdf, setIsDownloadingGeoPdf] = React.useState(false);
  const [isDownloadingGeotiff, setIsDownloadingGeotiff] = React.useState(false);
  const [downloadPngError, setDownloadPngError] = React.useState<string | null>(null);
  const [downloadGeoPdfError, setDownloadGeoPdfError] = React.useState<string | null>(null);
  const [downloadGeotiffError, setDownloadGeotiffError] = React.useState<string | null>(null);
  const [includeOverlaysPng, setIncludeOverlaysPng] = React.useState(false);
  const [includeOverlaysGeoPdf, setIncludeOverlaysGeoPdf] = React.useState(false);

  const [hasPendingDownload, setHasDownloadPending] = useStateWithDeps(false, [
    imageRef, // reset if the layer/source/feature changes
  ]);

  const pushNotification = usePushNotification();

  if (!featureDatum) {
    return null;
  }

  const sourceDetails: featureUtils.ImagerySourceDetails = featureUtils.imageAndSourceDetails(
    featureDatum,
    imageRef.layerKey,
    organization
  ).sourceDetails;

  let downloadPng: (() => Promise<void>) | null = null;
  const paidImagery = featureUtils.getPaidImagery(selectedFeature);
  const scenes = paidImagery?.get('scenes');
  const orderImageryState = featureUtils.getOrderImageryState(
    scenes,
    featureDatum,
    imageRef.layerKey
  );
  if (orderImageryState == featureUtils.ORDER_IMAGERY_STATE_PROCESSED) {
    downloadPng = async () => {
      setIsDownloadingPng(true);
      setDownloadPngError(null);
      try {
        const {sourceId, layerId} = layerUtils.parseLayerKey(imageRef.layerKey);
        const blob = await api.featureCollections
          .features(featureCollection.get('id'))
          .exportPng(
            selectedFeature.get('id'),
            sourceId,
            layerId,
            featureDatum.get('date'),
            includeOverlaysPng ? visibleOverlayConfigs : []
          );
        const filename = `${selectedFeature.getIn(['properties', 'name'])} - ${featureDatum.get(
          'date'
        )}.png`;
        saveAs(blob, filename);
        recordEvent('Downloaded PNG', {
          featureCollectionId: featureCollection.get('id'),
          featureId: selectedFeature.get('id'),
          layerId,
          date: featureDatum.get('date'),
        });
      } catch (error) {
        Sentry.captureException(error);
        setDownloadPngError('Error downloading image');
      } finally {
        setIsDownloadingPng(false);
      }
    };
  }

  let downloadGeotiff: (() => Promise<void>) | null = null;
  const canDownloadCogs = userUtils.atLeastUserRole(CONSTANTS.USER_ROLE_REGULAR, profile);
  const {sourceId, layerId} = layerUtils.parseLayerKey(imageRef.layerKey);
  // Very old feature data may have url (singular), not urls
  const hasCog =
    featureDatum.hasIn(['images', 'cogUrls', sourceId, layerId, 'urls']) ||
    featureDatum.hasIn(['images', 'cogUrls', sourceId, layerId, 'url']);

  // Webtile-only sources (e.g. WMTS-TRUECOLOR) have no cogUrls; their GeoTIFF is stitched on the
  // backend from the persistent imagery-central webtiles, so gate on the presence of a template url.
  // Mirrors GEOTIFF_EXPORT_WEBTILE_SOURCES in backend/common/cog_export_utils.py.
  const hasWebtileExport =
    GEOTIFF_EXPORT_WEBTILE_SOURCES.includes(sourceId) &&
    featureDatum.hasIn(['images', 'templateUrls', imageRef.layerKey]);

  let downloadGeoPdf: (() => Promise<void>) | null = null;
  const canDownloadGeoPdf = userUtils.atLeastUserRole(CONSTANTS.USER_ROLE_REGULAR, profile);

  // GeoTIFFs and GeoPDFs are both available for COG sources (GEOTIFF_EXPORT_ALLOWED_SOURCES) and
  // webtile-only sources (GEOTIFF_EXPORT_WEBTILE_SOURCES).
  if (hasCog || hasWebtileExport) {
    downloadGeotiff = async () => {
      setIsDownloadingGeotiff(true);
      setDownloadGeotiffError(null);
      try {
        const filename = `${selectedFeature.getIn(['properties', 'name'])} - ${featureDatum.get(
          'date'
        )}.tif`;

        const signedUrlResponse = await api.featureCollections
          .features(featureCollection.get('id'))
          .exportGeotiff(
            selectedFeature.get('id'),
            sourceId,
            layerId,
            featureDatum.get('date'),
            filename
          );

        const signedUrl = signedUrlResponse.get('data');
        if (signedUrl === null) {
          setHasDownloadPending(true);
          pushNotification({
            message: downloadPendingMessage,
            options: {intent: B.Intent.SUCCESS},
          });
        } else {
          window.open(signedUrl, '_blank'); // open new window for download
          recordEvent('Downloaded GeoTIFF', {
            featureCollectionId: featureCollection.get('id'),
            featureId: selectedFeature.get('id'),
            sourceId,
            layerId,
            date: featureDatum.get('date'),
          });
        }
      } catch (error) {
        Sentry.captureException(error);
        setDownloadGeotiffError('Error downloading GeoTIFF');
      } finally {
        setIsDownloadingGeotiff(false);
      }
    };
  }

  if (hasCog || hasWebtileExport) {
    downloadGeoPdf = async () => {
      setIsDownloadingGeoPdf(true);
      setDownloadGeoPdfError(null);
      try {
        const filename = `${selectedFeature.getIn(['properties', 'name'])} - ${featureDatum.get(
          'date'
        )}_${sourceId}_${layerId}.pdf`;
        const blob = await api.featureCollections
          .features(featureCollection.get('id'))
          .exportGeoPdf(
            selectedFeature.get('id'),
            sourceId,
            layerId,
            featureDatum.get('date'),
            includeOverlaysGeoPdf ? visibleOverlayConfigs : []
          );
        saveAs(blob, filename);
        recordEvent('Downloaded Geospatial PDF', {
          featureCollectionId: featureCollection.get('id'),
          featureId: selectedFeature.get('id'),
          sourceId,
          layerId,
          date: featureDatum.get('date'),
        });
      } catch (error) {
        Sentry.captureException(error);
        setDownloadGeoPdfError('Error downloading Geospatial PDF');
      } finally {
        setIsDownloadingGeoPdf(false);
      }
    };
  }

  const date = moment(featureDatum.get('date'));
  const formats = [
    downloadPng && 'PNG',
    downloadGeotiff && 'GeoTIFF',
    downloadGeoPdf && 'PDF',
  ].filter(Boolean);

  return (
    <div className={cs.layerBlock}>
      {position !== 'single' && (
        <span className={cs.layerBlockTitle}>
          {position === 'left' ? 'Left image' : 'Right image'}
        </span>
      )}

      <SidebarFieldGroup labelWidth="9.6rem">
        {sourceDetails && (
          <SidebarFieldRow
            label="Source"
            value={`${sourceDetails.operator} ${sourceDetails.name}`}
          />
        )}
        <SidebarFieldRow label="Capture">
          {imageryUtils.formatCaptureDate(date, imageRef.layerKey, {
            featureDatum,
            showCaptureTime: true,
          })}
        </SidebarFieldRow>
        {sourceDetails.resolution && (
          <SidebarFieldRow
            label="Resolution"
            value={`${parseFloat(sourceDetails.resolution.toFixed(3))}m`}
          />
        )}
      </SidebarFieldGroup>

      {formats.length > 0 && (
        <SidebarSection
          title="Downloads"
          meta={formats.join(' · ')}
          metaCollapsedOnly
          variant="card"
          defaultOpen={false}
          rightElement={<LearnMoreButton href={EXPORT_HELP_URL} />}
          rightElementHoverOnly
          className={cs.downloadsSection}
        >
          {downloadPng && (
            <DownloadFormat
              title="PNG"
              onDownload={downloadPng}
              isLoading={isDownloadingPng}
              isDisabled={isDownloadingPng}
              error={downloadPngError}
              note="PNGs are image files, exported at a reduced resolution, and are not georeferenced. These maps can be shared with landowners or stakeholders."
              includeOverlays={
                visibleOverlayConfigs.length > 0
                  ? {checked: includeOverlaysPng, onChange: setIncludeOverlaysPng}
                  : undefined
              }
            />
          )}
          {downloadGeotiff && (
            <DownloadFormat
              title="GeoTIFF"
              onDownload={() => downloadGeotiff()}
              isLoading={isDownloadingGeotiff}
              isDisabled={isDownloadingGeotiff || hasPendingDownload || !canDownloadCogs}
              disabledTooltip={
                !canDownloadCogs
                  ? "Your account does not have permission to download GeoTIFFs. Please contact your organization's administrator to request access."
                  : hasPendingDownload
                    ? downloadPendingMessage
                    : undefined
              }
              error={downloadGeotiffError}
              note="Cloud Optimized GeoTIFFs are georeferenced image files, exported at full resolution, and can be used in GIS applications."
            />
          )}
          {downloadGeoPdf && (
            <DownloadFormat
              title="Geospatial PDF"
              onDownload={() => downloadGeoPdf()}
              isLoading={isDownloadingGeoPdf}
              isDisabled={isDownloadingGeoPdf || !canDownloadGeoPdf}
              disabledTooltip={
                !canDownloadGeoPdf
                  ? "Your account does not have permission to download Geospatial PDFs. Please contact your organization's administrator to request access."
                  : undefined
              }
              error={downloadGeoPdfError}
              note="Geospatial PDFs are georeferenced PDFs, exported at a reduced resolution, that can be used in PDF viewers and some GIS applications."
              includeOverlays={
                visibleOverlayConfigs.length > 0
                  ? {checked: includeOverlaysGeoPdf, onChange: setIncludeOverlaysGeoPdf}
                  : undefined
              }
            >
              {includeOverlaysGeoPdf &&
                visibleOverlayConfigs.some(
                  (oc) => oc.feature_collection_id === CONSTANTS.REGRID_FEATURE_COLLECTION_ID
                ) && (
                  <SidebarParagraph className={classnames(cs.noteWithIcon, cs.fieldNote)}>
                    <B.Icon icon="warning-sign" intent={B.Intent.WARNING} size={11} />
                    Parcel Data will be excluded from this export.
                  </SidebarParagraph>
                )}
            </DownloadFormat>
          )}
        </SidebarSection>
      )}
    </div>
  );
};

const DownloadGif: React.FunctionComponent<
  React.PropsWithChildren<{
    imageRefs: [mapUtils.MapImageRef, mapUtils.MapImageRef];
    featureCollection: I.ImmutableOf<ApiFeatureCollection>;
    selectedFeature: I.ImmutableOf<ApiFeature>;
    visibleOverlayConfigs: OverlayConfig[];
  }>
> = ({imageRefs, featureCollection, selectedFeature, visibleOverlayConfigs}) => {
  const featureData = useFeatureDataForFeature(selectedFeature.get('id'));
  const pushNotification = usePushNotification();
  const paidImagery = featureUtils.getPaidImagery(selectedFeature);
  const scenes = paidImagery?.get('scenes');
  const featureDatums: I.ImmutableOf<ApiFeatureData>[] | null = React.useMemo(() => {
    if (!featureData) {
      return null;
    }

    const processedFoundFeatureDatums: I.ImmutableOf<ApiFeatureData>[] = imageRefs
      .map((imageRef) => {
        const cursorKey = featureUtils.getCursorKeyForLayerKey(
          featureCollection,
          imageRef.layerKey
        );
        // It is possible this featureDatum doesn't exist. When we switch between different properties
        // it can take a few renders for the imageRefs and featureData to update
        return featureData.findLast(
          (d) =>
            d!.get('types').includes(imageRef.layerKey) &&
            d!.get(cursorKey) === imageRef.cursor &&
            featureUtils.getOrderImageryState(scenes, d!, imageRef.layerKey) ===
              featureUtils.ORDER_IMAGERY_STATE_PROCESSED
        ) as I.ImmutableOf<ApiFeatureData> | undefined;
      })
      // Filter out any falsey featureDatum values
      .filter((fd): fd is I.ImmutableOf<ApiFeatureData> => !!fd);

    // If we don't have all of the featureData we need, we return null to hide the download GIF feature
    if (processedFoundFeatureDatums.length === imageRefs.length) {
      return processedFoundFeatureDatums;
    } else {
      return null;
    }
  }, [featureCollection, featureData, imageRefs, scenes]);

  const [isDownloadingGif, setIsDownloadingGif] = React.useState(false);
  const [includeOverlays, setIncludeOverlays] = React.useState(false);
  let downloadGif: (() => Promise<void>) | null = null;

  if (featureDatums) {
    downloadGif = async () => {
      setIsDownloadingGif(true);
      try {
        const gifScenes = imageRefs.map((imageRef) => {
          const {sourceId, layerId} = layerUtils.parseLayerKey(imageRef.layerKey);
          return {
            source_id: sourceId,
            layer_id: layerId,
            sensing_time: imageRef.cursor!,
          };
        });
        const blob = await api.featureCollections
          .features(featureCollection.get('id'))
          .exportGif(
            selectedFeature.get('id'),
            gifScenes,
            includeOverlays ? visibleOverlayConfigs : []
          );
        const filename = `${selectedFeature.getIn([
          'properties',
          'name',
        ])} - ${featureDatums.map((featureDatum) => featureDatum.get('date')).join(' ')}.gif`;
        saveAs(blob, filename);
        recordEvent('Downloaded GIF', {
          featureCollectionId: featureCollection.get('id'),
          featureId: selectedFeature.get('id'),
          gifScenes: JSON.stringify(gifScenes),
        });
      } catch (error) {
        pushNotification({
          message: `There was a problem generating a gif of the selected scenes.`,
          autoHideDuration: 3000,
          options: {
            intent: B.Intent.DANGER,
          },
        });
        Sentry.captureException(error);
      } finally {
        setIsDownloadingGif(false);
      }
    };
  }

  if (!downloadGif) {
    return null;
  }

  return (
    <div className={cs.layerBlock}>
      <span className={cs.layerBlockTitle}>Animated GIF</span>
      <DownloadFormat
        title="GIF"
        onDownload={() => downloadGif()}
        isLoading={isDownloadingGif}
        isDisabled={isDownloadingGif}
        note="GIFs are image files, exported at a reduced resolution, and are not georeferenced. These can be shared with landowners or stakeholders."
        includeOverlays={
          visibleOverlayConfigs.length > 0
            ? {checked: includeOverlays, onChange: setIncludeOverlays}
            : undefined
        }
      />
    </div>
  );
};

const PropertyDetailsSidebar: React.FunctionComponent<
  React.PropsWithChildren<{
    firebaseToken: string;
    overlayFeatureCollections: I.ImmutableListOf<ApiFeatureCollection>;
    visibleOverlayConfigs?: OverlayConfig[];
    featureCollection: I.ImmutableOf<ApiFeatureCollection>;
    project: I.ImmutableOf<ApiProject>;
    allFeatures: I.ImmutableOf<ApiFeature[]>;
    selectedFeatureId: number;
    role: ApiOrganizationUser['role'];
    organization: I.ImmutableOf<ApiOrganization>;
    organizationUsers: ApiOrganizationUser[];
    activeLayerKey?: string | null;
    layerLegendWidth?: number;
    batchPatchFeature: FeaturesActions['batchPatchFeature'];
    batchArchiveFeature: FeaturesActions['batchArchiveFeatures'];
    setTags: LoggedInUserActions['setOrganizationTags'];
    imageRefs: mapUtils.MapImageRefs;
  }>
> = ({
  firebaseToken,
  overlayFeatureCollections,
  visibleOverlayConfigs = [],
  featureCollection,
  project,
  allFeatures,
  selectedFeatureId,
  role,
  organization,
  organizationUsers,
  batchPatchFeature,
  batchArchiveFeature,
  setTags,
  activeLayerKey,
  imageRefs,
}) => {
  const navigate = useNavigate();
  const featureData = useFeatureDataForFeature(selectedFeatureId);
  const {reportEl, open: openReport} = React.useContext(ReportPortalContext);

  const canEdit = userUtils.isOwnerOrAdmin(role) || role === 'regular';
  const selectedFeature = allFeatures.find((f) => f!.get('id') === selectedFeatureId);
  const featureName = selectedFeature.getIn(['properties', 'name']).toString();
  const isMultiLocationProperty = multiFeaturePropertyUtils.isMultiLocationProperty(
    allFeatures,
    selectedFeature
  );
  const multiFeaturePropertyParts = React.useMemo(
    () => multiFeaturePropertyUtils.getMultiFeaturePropertyParts(allFeatures, selectedFeature),
    [allFeatures, selectedFeature]
  );

  const isParcelReportEnabled = React.useMemo(
    () => getIsParcelReportEnabled(selectedFeature, overlayFeatureCollections),
    [selectedFeature, overlayFeatureCollections]
  );

  const [isCustomizeTagsModalVisible, setIsCustomizeTagsModalVisible] = React.useState(false);

  const selectedFeatures = I.List(multiFeaturePropertyParts.map((p) => p.feature));

  const selectedPropertyPart = multiFeaturePropertyParts.find(
    (p) => p.feature.get('id') === selectedFeature.get('id')
  )!;

  const areaUnit = getAreaUnit(organization);
  const areaUnitName = areaUnit === 'areaHectare' ? 'hectares' : 'acres';

  const propertyAreaInDisplayUnit = React.useMemo(() => {
    const areaInM2 = multiFeaturePropertyParts.reduce(
      (acc, p) => acc + UTMArea(p.feature.toJS()),
      0
    );

    return conversionUtils.convert(areaInM2, UNIT_AREA_M2, areaUnit);
  }, [multiFeaturePropertyParts, areaUnit]);

  const partAreaInDisplayUnit = React.useMemo(
    () => conversionUtils.convert(UTMArea(selectedFeature.toJS()), UNIT_AREA_M2, areaUnit),
    [selectedFeature, areaUnit]
  );

  const appProperties = selectedFeature.getIn(['properties', APP_PROPERTIES_KEY]);

  const allowStreaming =
    layerUtils.isLayerKeyHighResTruecolor(activeLayerKey) ||
    layerUtils.isMultispectralLayerKey(activeLayerKey) ||
    // we want to allow streaming for PLANET-BASEMAP as well
    activeLayerKey === PLANET_BASEMAP_HIGH_RES_TRUECOLOR;

  const wmtsUrl = allowStreaming && appProperties?.get(CONSTANTS.APP_PROPERTY_WMTS_URL);

  const xyzUrls = React.useMemo(() => {
    const streamimageryApiKey =
      allowStreaming && appProperties?.get(CONSTANTS.APP_PROPERTY_STREAMIMAGERY_API_KEY);

    if (!streamimageryApiKey || !featureData) {
      return null;
    }

    return imageRefs
      .map((imageRef) => {
        const cursorKey = featureUtils.getCursorKeyForLayerKey(
          featureCollection,
          imageRef.layerKey
        );
        const featureDatum = featureData.findLast(
          (d) =>
            d!.get('types').includes(imageRef.layerKey) && d!.get(cursorKey) === imageRef.cursor
        );
        if (!featureDatum) {
          return null;
        }

        const templateUrl = featureDatum.getIn([
          'images',
          'templateUrls',
          imageRef.layerKey,
          'tiles',
          0,
        ]);
        if (!templateUrl || !mapUtils.isTilerUrl(templateUrl)) {
          return null;
        }
        return mapUtils.formatTileUrl(templateUrl, streamimageryApiKey, true, (url: URL) => {
          url.searchParams.delete('feature_id');
          url.searchParams.set('lens_id', selectedFeature.getIn(['properties', 'lensId']));
          return url;
        });
      })
      .filter((url) => url != null);
  }, [featureCollection, selectedFeature, featureData, imageRefs, allowStreaming, appProperties]);

  // The layer section owns the header its blocks used to carry, so it has to
  // know up front whether any of them will render — otherwise switching
  // properties flashes an empty accordion.
  const showSourceDetails = imageRefs.some(
    (imageRef) => !!findFeatureDatumForImageRef(featureData, featureCollection, imageRef)
  );

  const uniqueLegendLayers: LayerInfo[] = Array.from(
    new Set(
      imageRefs
        .map((imageRef) => layerUtils.getLayer(imageRef.layerKey))
        .filter((layer) => layer && 'renderLayerLegend' in layer && layer.renderLayerLegend)
    )
  );

  const lensIdField = (
    <SidebarFieldRow
      label="Lens ID"
      labelAction={
        <LearnMoreButton href="https://lens.tech/support/article/91-maintaining-your-lensids" />
      }
      copyValue={selectedFeature.getIn(['properties', 'lensId']) || undefined}
      truncate
    >
      <code className={cs.mono}>
        {selectedFeature.getIn(['properties', 'lensId']) || 'unknown'}
      </code>
    </SidebarFieldRow>
  );

  const excludedFeatureProperties = featureUtils.FeatureAttributes.all;

  const customFeaturePropertyKeys = React.useMemo(
    () =>
      selectedFeature
        .get('properties')
        .keySeq()
        .filter((x) => !excludedFeatureProperties.includes(x!))
        .sort((propertyKeyA, propertyKeyB) =>
          featureUtils.alphanumericSort(propertyKeyA, propertyKeyB)
        )
        .toList(),
    [excludedFeatureProperties, selectedFeature]
  );

  const customFeatureProperties =
    customFeaturePropertyKeys.size > 0 ? (
      <SidebarSection
        title="Attributes"
        meta={String(customFeaturePropertyKeys.size)}
        metaCollapsedOnly
        variant="card"
        defaultOpen={false}
        className={cs.attributesSection}
      >
        <dl className={cs.customAttributes}>
          {
            customFeaturePropertyKeys.map((key) => (
              <div key={key} className={cs.customAttribute}>
                <dt>{key}</dt>
                <dd>{String(selectedFeature.get('properties').get(key!))}</dd>
              </div>
            )) as unknown as ReactNode
          }
        </dl>
      </SidebarSection>
    ) : null;

  const showStreamingSection = !!wmtsUrl && canEdit;

  const propertySummary = `${conversionUtils.numberWithCommas(
    propertyAreaInDisplayUnit.toFixed(2)
  )} ${areaUnitName}`;

  return (
    <SidebarPanel
      header={
        <SidebarHeader
          title={featureName}
          subtitle={
            multiFeaturePropertyParts.length > 1
              ? `${multiFeaturePropertyParts.length} locations · ${propertySummary}`
              : propertySummary
          }
          hoverActions={
            canEdit && (
              <B.Tooltip content="Edit property name">
                <B.Button
                  icon="edit"
                  minimal
                  small
                  onClick={() => {
                    const editedName = prompt('Enter a new property name:', featureName);
                    if (!editedName || editedName === featureName) {
                      return;
                    }
                    batchPatchFeature({
                      featureCollectionId: featureCollection.get('id'),
                      features: selectedFeatures,
                      changes: I.fromJS({properties: {name: editedName}}),
                    });
                  }}
                />
              </B.Tooltip>
            )
          }
        />
      }
    >
      <SidebarSection title="Property details" bordered={false}>
        <SidebarFieldGroup>
          {/* If this isn't a multi location property, show the Lens ID above the property level fields. */}
          {!isMultiLocationProperty && lensIdField}
          {multiFeaturePropertyParts.length > 1 && (
            <>
              {/** If we are viewing part of a multi-features property, allow users with editing access
              to change the part name. This updates the title and map labels in the note report. */}
              <SidebarFieldRow
                label="Location name"
                onEdit={
                  canEdit
                    ? () => {
                        const editedName = prompt(
                          'Enter a new location name:',
                          String(selectedPropertyPart.partName ?? '')
                        );
                        if (!editedName || editedName === selectedPropertyPart.partName) {
                          return;
                        }
                        batchPatchFeature({
                          featureCollectionId: featureCollection.get('id'),
                          features: I.List([selectedFeature]),
                          changes: I.fromJS({properties: {multiFeaturePartName: editedName}}),
                        });
                      }
                    : undefined
                }
              >
                {selectedPropertyPart.partName || <SidebarEmptyValue />}
              </SidebarFieldRow>

              {/** If we are viewing part of a multi-features property, display the area of the selected part. */}
              <SidebarFieldRow
                label="Location area"
                value={`${conversionUtils.numberWithCommas(
                  partAreaInDisplayUnit.toFixed(2)
                )} ${areaUnitName}`}
              />
            </>
          )}
          {/* If this is a multi location property, show the Lens ID below other location level fields. */}
          {isMultiLocationProperty && lensIdField}
          <TagField
            title={featureUtils.APP_PROPERTY_NAMES.tagIds}
            features={I.List([selectedFeature])}
            featureCollection={featureCollection}
            // Modify the batchPatchFeature logic so that we only update the
            // selected feature. This allows the user to set tags on a specific
            // part of a multi-location property.
            batchPatchFeature={(args) =>
              batchPatchFeature({
                ...args,
                features: I.List([selectedFeature]),
              })
            }
            role={role}
            openCustomizeTagsModal={() => {
              setIsCustomizeTagsModalVisible(true);
            }}
            organization={organization}
          />
          <AssigneeField
            title={featureUtils.APP_PROPERTY_NAMES.assigneeEmail}
            organizationUsers={organizationUsers}
            emails={featureUtils.resolveAssignees(
              appProperties?.get('assigneeEmails'),
              appProperties?.get('assigneeEmail')
            )}
            featureCollection={featureCollection}
            features={I.List([selectedFeature])}
            batchPatchFeature={batchPatchFeature}
            canEdit={canEdit}
          />
          <SidebarFieldRow
            label={featureUtils.APP_PROPERTY_NAMES.reportingDueDate}
            trailingAction={
              canEdit ? (
                <DueDateMenu
                  featureCollection={featureCollection}
                  features={I.List([selectedFeature])}
                  batchPatchFeature={batchPatchFeature}
                  value={appProperties?.get('reportingDueDate') ?? ''}
                >
                  <B.Tooltip content="Edit due date">
                    <B.Button icon="edit" minimal small />
                  </B.Tooltip>
                </DueDateMenu>
              ) : undefined
            }
          >
            {appProperties?.get('reportingDueDate') || <SidebarEmptyValue />}
          </SidebarFieldRow>
          {/* If custom, user-provided properties exist on the GeoJSON Feature, they will be displayed here. */}
          {customFeatureProperties}
        </SidebarFieldGroup>
      </SidebarSection>

      {showSourceDetails && (
        <SidebarSection title="Layer details">
          {imageRefs.length === 1 && (
            <SourceInformation
              position="single"
              imageRef={imageRefs[0]}
              organization={organization}
              featureCollection={featureCollection}
              selectedFeature={selectedFeature}
              visibleOverlayConfigs={visibleOverlayConfigs}
            />
          )}
          {imageRefs.length === 2 && (
            <>
              <SourceInformation
                position="left"
                imageRef={imageRefs[0]}
                organization={organization}
                featureCollection={featureCollection}
                selectedFeature={selectedFeature}
                visibleOverlayConfigs={visibleOverlayConfigs}
              />
              <SourceInformation
                position="right"
                imageRef={imageRefs[1]}
                organization={organization}
                featureCollection={featureCollection}
                selectedFeature={selectedFeature}
                visibleOverlayConfigs={visibleOverlayConfigs}
              />
              <DownloadGif
                imageRefs={imageRefs}
                featureCollection={featureCollection}
                selectedFeature={selectedFeature}
                visibleOverlayConfigs={visibleOverlayConfigs}
              />
            </>
          )}
        </SidebarSection>
      )}

      {uniqueLegendLayers.length > 0 && (
        <SidebarSection
          title="Layer legend"
          id={PROPERTY_DETAILS_LAYER_LEGEND_SECTION_ID}
          className={cs.legendSection}
        >
          {uniqueLegendLayers.map(
            (layer, i) =>
              layer &&
              'renderLayerLegend' in layer &&
              !!layer.renderLayerLegend && (
                <div className={cs.legend} key={'layersection' + i}>
                  <div className={cs.legendTitle}>{layer.display}</div>
                  {layer.renderLayerLegend({includeExtendedDescription: true})}
                </div>
              )
          )}
        </SidebarSection>
      )}

      {showStreamingSection && (
        <SidebarSection
          title="Imagery streaming"
          rightElement={<LearnMoreButton href={STREAMING_HELP_URL} />}
          rightElementHoverOnly
        >
          <SidebarFieldGroup>
            <div>
              <SidebarFieldRow
                label="WMTS"
                layout="stacked"
                copyValue={decodeURI(wmtsUrl)}
                truncate
              >
                <code className={cs.mono}>{decodeURI(wmtsUrl)}</code>
              </SidebarFieldRow>
              <SidebarParagraph className={cs.fieldNote}>
                Copy this web map tile server (WMTS) link to add to GIS. This link will include all
                imagery ordered for this property.
              </SidebarParagraph>
            </div>
            {xyzUrls?.length === 1 && (
              <div>
                <SidebarFieldRow
                  label="XYZ"
                  layout="stacked"
                  copyValue={decodeURI(xyzUrls[0]!)}
                  truncate
                >
                  <code className={cs.mono}>{decodeURI(xyzUrls[0]!)}</code>
                </SidebarFieldRow>
                <SidebarParagraph className={cs.fieldNote}>
                  Copy this XYZ URL to add to GIS. It will include just one selected scene.
                </SidebarParagraph>
              </div>
            )}
            {xyzUrls?.length === 2 && (
              <div>
                <SidebarFieldRow
                  label="XYZ (Left)"
                  layout="stacked"
                  copyValue={decodeURI(xyzUrls[0]!)}
                  truncate
                >
                  <code className={cs.mono}>{decodeURI(xyzUrls[0]!)}</code>
                </SidebarFieldRow>
                <SidebarFieldRow
                  label="XYZ (Right)"
                  layout="stacked"
                  copyValue={decodeURI(xyzUrls[1]!)}
                  truncate
                >
                  <code className={cs.mono}>{decodeURI(xyzUrls[1]!)}</code>
                </SidebarFieldRow>
                <SidebarParagraph className={cs.fieldNote}>
                  Copy an XYZ URL to add to GIS. It will include just one selected scene.
                </SidebarParagraph>
              </div>
            )}
          </SidebarFieldGroup>
        </SidebarSection>
      )}

      {isParcelReportEnabled && (
        <SidebarSection title="Parcel owner report" defaultOpen={false}>
          <SidebarParagraph>
            Generate a report of this property's parcel information, along with the parcel data of
            contiguous landowners.
          </SidebarParagraph>
          <B.Button
            className={cs.blockButton}
            text="Generate report"
            outlined
            fill
            onClick={(ev) => {
              openReport(ev.ctrlKey || ev.metaKey);
            }}
          />
        </SidebarSection>
      )}

      {/* Outside the section above: collapsing it would unmount the portal and
        blank the open report window. */}
      {reportEl &&
        ReactDOM.createPortal(
          <ParcelReportWindow
            firebaseToken={firebaseToken}
            organization={organization}
            projectId={project.get('id')}
            feature={selectedFeature}
            featureCollection={featureCollection}
            overlayFeatureCollections={overlayFeatureCollections}
            multiFeaturePropertyParts={multiFeaturePropertyParts}
          />,
          reportEl
        )}

      {userUtils.isOwnerOrAdmin(role) && (
        <div className={cs.dangerZone}>
          <B.Button
            icon="trash"
            text="Delete property"
            intent={B.Intent.DANGER}
            outlined
            fill
            onClick={async () => {
              if (
                confirm(
                  'Are you sure you want to delete this property? It will no longer be visible in Lens, so we recommend saving any reports prior to deleting.'
                )
              ) {
                await batchArchiveFeature({
                  featureCollectionId: featureCollection.get('id'),
                  features: I.List([selectedFeature]),
                });
                /**
                 * Even though we’re only archiving one feature, we keep this
                 * shape consistent with the Smartlook event triggered from the
                 * SelectedActions component so the data is grouped together.
                 */
                recordEvent('Archived features', {
                  featureCollectionId: featureCollection.get('id'),
                  featureCount: 1,
                });
                navigate(routeUtils.makeProjectDashboardUrl(organization, project));
              }
            }}
          />
        </div>
      )}

      {isCustomizeTagsModalVisible && (
        <CustomizeTagsModal
          organization={organization}
          setTags={setTags}
          onClose={() => setIsCustomizeTagsModalVisible(false)}
          kind={tagUtils.TAG_KIND.FEATURE}
        />
      )}
    </SidebarPanel>
  );
};

function getIsParcelReportEnabled(
  feature: I.ImmutableOf<ApiFeature>,
  overlayFeatureCollections: I.ImmutableOf<ApiFeatureCollection[]>
) {
  return (
    overlayFeatureCollections.findIndex(
      (fc) => fc?.get('id') === CONSTANTS.REGRID_FEATURE_COLLECTION_ID
    ) > -1 && featureInRegridCoverage(feature)
  );
}

export default PropertyDetailsSidebar;
